// Per-user visual preferences (accent colour, table density). Applied to
// <html> via CSS custom properties + data attributes so any component that
// opts in (data-density-* rules in globals.css) picks them up automatically.
const KEY = "immomap:layout:v1";

export const DEFAULTS = {
  accent: "#007AFF",      // iOS blue
  density: "normal",      // compact | normal | comfortable
};

export const ACCENT_SWATCHES = [
  { value: "#007AFF", label: "Blu" },
  { value: "#34C759", label: "Verde" },
  { value: "#7C3AED", label: "Viola" },
  { value: "#FF9500", label: "Arancio" },
  { value: "#FF3B30", label: "Rosso" },
  { value: "#12466F", label: "Navy" },
];

export const DENSITY_OPTIONS = [
  { value: "compact",     label: "Compatta",     desc: "Righe più fitte, più righe visibili" },
  { value: "normal",      label: "Normale",      desc: "Bilanciamento predefinito" },
  { value: "comfortable", label: "Confortevole", desc: "Più spazio verticale, più leggibile" },
];

// SECURITY NOTE: layout prefs are UI-only (accent hex + density enum).
// We validate the shape on read so a tampered value can't inject CSS
// or break `applyLayout()`. Nothing sensitive lives here.
const HEX_RE = /^#[0-9a-fA-F]{6}$/;
const VALID_DENSITY = new Set(["compact", "normal", "comfortable"]);

const sanitize = (obj) => {
  if (!obj || typeof obj !== "object") return { ...DEFAULTS };
  return {
    accent:  HEX_RE.test(obj.accent)               ? obj.accent  : DEFAULTS.accent,
    density: VALID_DENSITY.has(obj.density)        ? obj.density : DEFAULTS.density,
  };
};

export const loadLayout = () => {
  if (typeof window === "undefined") return { ...DEFAULTS };
  try {
    const raw = window.localStorage.getItem(KEY);
    return raw ? sanitize(JSON.parse(raw)) : { ...DEFAULTS };
  } catch (err) {
    console.warn("[layoutPrefs] load failed:", err);
    return { ...DEFAULTS };
  }
};

export const saveLayout = (partial) => {
  const next = sanitize({ ...loadLayout(), ...(partial || {}) });
  try {
    window.localStorage.setItem(KEY, JSON.stringify(next));
  } catch (err) {
    console.warn("[layoutPrefs] save failed:", err);
  }
  applyLayout(next);
  return next;
};

// Hex "#RRGGBB" → "H S% L%" (formato richiesto da Tailwind `hsl(var(--accent))`).
// FIX: prima veniva scritto direttamente l'hex nella CSS variable, ma Tailwind lo
// avvolge in `hsl(...)` — passare un hex a `hsl()` restituisce un colore invalido
// e il background del hover finiva trasparente, rendendo illeggibili le voci
// selezionate nei menu a discesa (testo bianco su sfondo bianco).
const hexToHsl = (hex) => {
  const m = /^#([0-9a-fA-F]{6})$/.exec(hex || "");
  if (!m) return "211 100% 50%";
  const r = parseInt(m[1].slice(0, 2), 16) / 255;
  const g = parseInt(m[1].slice(2, 4), 16) / 255;
  const b = parseInt(m[1].slice(4, 6), 16) / 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  const l = (max + min) / 2;
  let h = 0, s = 0;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    if (max === r) h = (g - b) / d + (g < b ? 6 : 0);
    else if (max === g) h = (b - r) / d + 2;
    else h = (r - g) / d + 4;
    h /= 6;
  }
  return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
};

export const applyLayout = (prefs = loadLayout()) => {
  if (typeof document === "undefined") return;
  const hsl = hexToHsl(prefs.accent);
  // Aggiorna sia `--accent` (usato dai menu shadcn per hover/focus) sia `--ring`
  // (focus ring). Non tocchiamo `--primary` per non alterare i CTA principali.
  document.documentElement.style.setProperty("--accent", hsl);
  document.documentElement.style.setProperty("--ring", hsl);
  // Il colore hex "grezzo" resta disponibile come `--brand-accent` per chi vuole
  // usare direttamente il valore (senza il wrapping hsl()).
  document.documentElement.style.setProperty("--brand-accent", prefs.accent);
  document.documentElement.setAttribute("data-density", prefs.density || "normal");
};
